import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { observable } from 'mobx';
import { Button, Intent } from '@blueprintjs/core';
import * as EmailValidator from 'email-validator';
import Toast from 'helpers/toaster';
import FormDrawer from 'components/FormDrawer';
import { FormSection, FieldError } from 'components/FormBits';

const FIELDS = ['name', 'email', 'phone', 'address'];

// Field -> input id, in the order they appear (the first with a problem gets focus).
const FIELD_IDS = {
  name: 'supplier-name',
  email: 'supplier-email',
  phone: 'supplier-phone',
  address: 'supplier-address',
};

const blank = (value) => !value || !String(value).trim();

// Edit a supplier's details (pages/suppliers/show.js), in a side panel. Works
// on a copy: Cancel leaves the supplier as it was; Save sends the copy.
@inject('supplierStore') @observer
class EditSupplierPanel extends Component {
  @observable draft = {};
  @observable errors = {};

  componentDidMount() {
    if (this.props.isOpen) this.reset();
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) this.reset();
  }

  reset() {
    const { attributes } = this.props.supplier;
    const draft = {};
    FIELDS.forEach((key) => { draft[key] = attributes[key] || ''; });
    this.draft = draft;
    this.errors = {};
  }

  setField(key, value) {
    this.draft[key] = value;
    if (this.errors[key]) this.errors = { ...this.errors, [key]: null };
  }

  validate() {
    const { draft } = this;
    const errors = {};
    if (blank(draft.name)) errors.name = "Enter the supplier's name.";
    if (!EmailValidator.validate(String(draft.email).trim())) errors.email = 'Enter an email address, like orders@example.com.';
    if (blank(draft.phone)) errors.phone = 'Enter a phone number.';
    if (blank(draft.address)) errors.address = "Enter the supplier's address.";
    return errors;
  }

  save = () => {
    const { supplier, supplierStore, onClose } = this.props;
    if (supplierStore.isSavingSupplier) return;
    const errors = this.validate();
    this.errors = errors;
    const first = Object.keys(FIELD_IDS).find((key) => errors[key]);
    if (first) {
      const element = document.getElementById(FIELD_IDS[first]);
      if (element) element.focus();
      return;
    }
    const attributes = { ...supplier.attributes };
    FIELDS.forEach((key) => { attributes[key] = String(this.draft[key]).trim(); });
    supplierStore
      .updateSupplier({ id: supplier.id, attributes })
      .then(() => {
        Toast.show({ message: `${attributes.name} saved.`, intent: Intent.SUCCESS });
        onClose();
      })
      .catch((error) => {
        const data = error && error.response && error.response.data;
        const reason = data && (data.errors || data.error);
        Toast.show({
          message: reason ? (Array.isArray(reason) ? reason.join(', ') : reason) : "Couldn't save the supplier. Try again.",
          intent: Intent.DANGER,
        });
      });
  };

  textField(key, label, { type = 'text', placeholder, autoComplete } = {}) {
    const id = FIELD_IDS[key];
    const error = this.errors[key];
    return (
      <div className={`bp3-form-group${error ? ' has-error' : ''}`}>
        <label className="fb-label" htmlFor={id}>
          {label}
          <span className="fb-required" aria-hidden="true"> *</span>
        </label>
        <input
          id={id}
          type={type}
          className="bp3-input bp3-fill"
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-required="true"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          value={this.draft[key] || ''}
          onChange={(event) => this.setField(key, event.target.value)}
        />
        <FieldError id={`${id}-error`}>{error}</FieldError>
      </div>
    );
  }

  render() {
    const { isOpen, onClose, supplierStore } = this.props;
    const busy = Boolean(supplierStore.isSavingSupplier);
    return (
      <FormDrawer
        title="Edit details"
        icon="edit"
        tone="customers"
        size="min(760px, 100vw)"
        isOpen={isOpen}
        onClose={onClose}
        busy={busy}
        footer={
          <>
            <Button text="Cancel" onClick={onClose} disabled={busy} />
            <Button intent={Intent.PRIMARY} icon="floppy-disk" text="Save changes" loading={busy} onClick={this.save} />
          </>
        }
      >
        <FormSection title="Supplier" hint="Who you buy parts and materials from.">
          {this.textField('name', 'Name', { placeholder: 'e.g. Wolseley Plumbing', autoComplete: 'organization' })}
        </FormSection>
        <FormSection title="Contact" hint="How to reach them.">
          <div className="fb-grid">
            {this.textField('email', 'Email', { type: 'email', placeholder: 'orders@example.com', autoComplete: 'email' })}
            {this.textField('phone', 'Phone', { type: 'tel', placeholder: '000-000-0000', autoComplete: 'tel' })}
          </div>
          {this.textField('address', 'Address', { placeholder: 'Street, city', autoComplete: 'street-address' })}
        </FormSection>
      </FormDrawer>
    );
  }
}

export default EditSupplierPanel;
